import React, { useEffect, useState } from "react";

import { convertEnergy2 } from "../../../../utils/dataAndFormulas/convertEnergy";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { formatWhole, formatMagnitude, formatMagnitudeAdaptive, formatPercentage } from "../../../../utils/numberFormat";
import "./FuelSummary.scss";

const FuelsSummary = ({
  filters,
  site,
  valueBaseline,
  useAuth,
  fuelsController,
  totales, // ✅ Prop con datos ya calculados desde otro componente
  selectorSlot,
  exportSlot,
}) => {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  // 🔹 Estado local para guardar los totales anuales procesados
  const [data, setData] = useState({
    total: 0,
    biocombustibles: 0,
    cantidad_vehiculos: 0,
    intensidad: { valor: 0 },
  });

  const [results, setResults] = useState({});

  const formatByRule = (item) => {
    switch (item.rule) {
      case "whole":
        return formatWhole(item.value);
      case "adaptive":
        return formatMagnitudeAdaptive(item.value);
      case "magnitude":
      default:
        return formatMagnitude(item.value);
    }
  };

  // 🔹 useEffect: se ejecuta cuando cambian filtros o totales
  useEffect(() => {
    const fetchData = async () => {
      try {
        // console.log("📊 Totales recibidos en FuelsSummary:", totales, filters);

        // ✅ PRIORIDAD 1: usar los datos ya calculados en EnergyKPIsDashBoard
        if (totales && Object.keys(totales).length > 0) {
          setResults(totales);

          // // 🔹 Extrae todos los meses del año (Object.values = array de meses)
          const getEnergyInMWh = (kpi, source) => {
            if (!kpi) return 0;
            return kpi.unit === "MWh"
              ? kpi.total
              : convertEnergy2(kpi.total, kpi.unit, "MWh", source,true);
          };

          // 🔹 Suma total de combustibles (MWh)
          const total = totales?.total_fuel_mwh[filters.year]?.total || 0;

          // 🔹 Suma total de biocombustibles (MWh)
          const biodiesel =
            getEnergyInMWh(totales?.ethanol[filters.year], "ethanol") / total;

            const ethanol =
            getEnergyInMWh(totales?.biodiesel[filters.year], "biodiesel") / total;


            const electricity =
            getEnergyInMWh(totales?.electricity_vehicles[filters.year], "electricity_vehicles") / total;

const biocombustibles=biodiesel+ethanol+electricity;
          // 🔹 Suma total de vehículos del año
          const cantidad_vehiculos =
            totales?.total_vehicles[filters.year]?.total;

          // 🔹 Calcula la intensidad: energía total en MWh equivalente por vehículo
          const intensidadValor = total / cantidad_vehiculos;

          // 🔹 Actualiza el estado con los resultados del año
          setData({
            total,
            biocombustibles,
            cantidad_vehiculos,
            intensidad: { valor: intensidadValor },
          });
        }
      } catch (error) {
        console.error("❌ Error al traer resumen de combustibles:", error);
      }
    };

    fetchData();
  }, [filters, site, valueBaseline, fuelsController, totales]);

  // 🔹 Porcentaje de biocombustibles sobre el total
  const percentBiocombustible =
    data.total > 0 ? (data.biocombustibles / data.total) * 100 : 0;

  // 🔹 Define las tarjetas del resumen (orden y visual)
  const resumenItems = [
    {
      title: t("total_fuels"),
      //subtitle:"(unidad de medida:  Mwh)",
      value: data.total,
      unit: "MWhe",
      rule: "whole",
      secondary: "",
      progress: null,
      color: "grey",
      hasProgress: false,
    },
    {
      title: t("biofuels"),
      //subtitle:"(unidad de medida:  Mwh)",
      value: data.biocombustibles,
      unit: "MWhe",
      rule: "whole",
      // Muestra el porcentaje calculado
      secondary: formatPercentage(percentBiocombustible),
      progress: percentBiocombustible,
      //legend:"La línea refleja el porcentaje entre total biocombustible/ sobre el Total de combustibles",
      color: "grey",
      hasProgress: true, // 🔹 esta tarjeta tiene barra de progreso
    },
    {
      title: t("vehicle_count"),
      //subtitle:"(unidad de medida:  unidades)",
      value: Math.round(data.cantidad_vehiculos),
      unit: "unidades",
      rule: "whole",
      secondary: "",
      progress: null,
      color: "grey",
      hasProgress: false,
    },
    {
      title: t("intensity"),
      // subtitle:"(unidad de medida:  Kwh/vehiculo)",
      value: data.intensidad.valor,
      unit: "MWhe / Vehículo",
      rule: "magnitude",
      secondary: "",
      progress: null,
      color: "grey",
      hasProgress: false,
    },
  ];

  // 🔹 Renderiza las tarjetas visuales
  return (
    <div className="resumen-grid">
      {resumenItems.map((item, index) => (
        <div
          key={index}
          className={`resumen-card-col${index === 0 ? " resumen-card-col--first" : ""}`}
        >
          <div className={`resumen-slot-row${index === resumenItems.length - 1 ? " resumen-slot-row--right" : ""}`}>
            {index === 0 && selectorSlot}
            {index === resumenItems.length - 1 && exportSlot}
          </div>
          <div
            className={`resumen-card fuel-card ${
              !item.hasProgress ? "no-progress" : ""
            }`}
          >
            <div className="card-meta">
              <div className="card-title-row">
                <span className="card-title">{item.title}</span>
              </div>
              <div>
                {item.subtitle && (
                  <span className="card-subtitle">{item.subtitle}</span>
                )}
              </div>
            </div>

            <div className="card-header">
              <span className="card-value">
                {formatByRule(item)}
                <span className="card-unit">{item.unit}</span>
              </span>
              {item.secondary && (
                <div className="percentage-text">{item.secondary}</div>
              )}
            </div>

            {item.hasProgress && item.progress !== null && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.round(item.progress || 0)}%`,
                      backgroundColor: item.color || "#648EBC",
                    }}
                  />
                </div>
                {item.legend && (
                  <div className="progress-legend">{item.legend}</div>
                )}
              </div>
            )}

            <div className="empty-space"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FuelsSummary;
